import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Settings,
  VolumeX,
  Volume2,
  Shield,
  Pin,
  X,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import { ArenaRules, PinnedMessage } from '@/hooks/useArenaModerationTools';
import { cn } from '@/lib/utils';

interface ArenaModerationPanelProps {
  isHost: boolean;
  rules: ArenaRules;
  pinnedMessages: PinnedMessage[];
  isMuteAllActive: boolean;
  onToggleMuteAll: () => void;
  onUpdateRules: (rules: ArenaRules) => void;
  onAddCustomRule: (rule: string) => void;
  onRemoveCustomRule: (index: number) => void;
  onUnpinMessage: (messageId: string) => void;
}

export const ArenaModerationPanel: React.FC<ArenaModerationPanelProps> = ({
  isHost,
  rules,
  pinnedMessages,
  isMuteAllActive,
  onToggleMuteAll,
  onUpdateRules,
  onAddCustomRule,
  onRemoveCustomRule,
  onUnpinMessage,
}) => {
  const [newRule, setNewRule] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  if (!isHost) return null;

  const handleAddRule = () => {
    if (newRule.trim()) {
      onAddCustomRule(newRule.trim());
      setNewRule('');
    }
  };

  const toggleRule = (key: keyof Omit<ArenaRules, 'customRules'>) => {
    onUpdateRules({
      ...rules,
      [key]: !rules[key],
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full w-10 h-10"
        >
          <Settings className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Moderation Tools
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Mute All Toggle */}
          <div className="p-4 rounded-xl bg-muted">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isMuteAllActive ? (
                  <VolumeX className="w-5 h-5 text-destructive" />
                ) : (
                  <Volume2 className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <p className="font-medium">Mute All</p>
                  <p className="text-xs text-muted-foreground">
                    Silence all participants
                  </p>
                </div>
              </div>
              <Button
                variant={isMuteAllActive ? 'destructive' : 'outline'}
                size="sm"
                onClick={onToggleMuteAll}
              >
                {isMuteAllActive ? 'Unmute' : 'Mute All'}
              </Button>
            </div>
          </div>

          {/* Arena Rules */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Arena Rules
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="noHateSpeech" className="text-sm">
                  No hate speech
                </Label>
                <Switch
                  id="noHateSpeech"
                  checked={rules.noHateSpeech}
                  onCheckedChange={() => toggleRule('noHateSpeech')}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="beRespectful" className="text-sm">
                  Be respectful
                </Label>
                <Switch
                  id="beRespectful"
                  checked={rules.beRespectful}
                  onCheckedChange={() => toggleRule('beRespectful')}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="stayOnTopic" className="text-sm">
                  Stay on topic
                </Label>
                <Switch
                  id="stayOnTopic"
                  checked={rules.stayOnTopic}
                  onCheckedChange={() => toggleRule('stayOnTopic')}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="noSpamming" className="text-sm">
                  No spamming
                </Label>
                <Switch
                  id="noSpamming"
                  checked={rules.noSpamming}
                  onCheckedChange={() => toggleRule('noSpamming')}
                />
              </div>

              {/* Custom Rules */}
              {rules.customRules.length > 0 && (
                <div className="pt-2 border-t border-border space-y-2">
                  {rules.customRules.map((rule, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="truncate">{rule}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => onRemoveCustomRule(index)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Custom Rule */}
              <div className="flex gap-2 pt-2">
                <Input
                  placeholder="Add custom rule..."
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddRule()}
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleAddRule}
                  disabled={!newRule.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Pinned Messages */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Pin className="w-4 h-4 text-blue-500" />
              Pinned Messages ({pinnedMessages.length})
            </h4>
            {pinnedMessages.length > 0 ? (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {pinnedMessages.map((message) => (
                  <div
                    key={message.id}
                    className="flex items-start gap-2 p-2 rounded-lg bg-muted text-sm"
                  >
                    <Pin className="w-3 h-3 mt-1 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs text-muted-foreground">
                        {message.sender_name}
                      </p>
                      <p className="line-clamp-2">{message.content}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => onUnpinMessage(message.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No pinned messages. Long-press a message to pin it.
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Rules display for non-hosts
interface ArenaRulesDisplayProps {
  rules: ArenaRules;
  className?: string;
}

export const ArenaRulesDisplay: React.FC<ArenaRulesDisplayProps> = ({
  rules,
  className,
}) => {
  const activeRules = [
    rules.noHateSpeech && 'No hate speech',
    rules.beRespectful && 'Be respectful',
    rules.stayOnTopic && 'Stay on topic',
    rules.noSpamming && 'No spamming',
    ...rules.customRules,
  ].filter(Boolean);

  if (activeRules.length === 0) return null;

  return (
    <div
      className={cn(
        'p-3 rounded-lg bg-muted/50 border border-border',
        className
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium">Arena Rules</span>
      </div>
      <ul className="text-xs text-muted-foreground space-y-1">
        {activeRules.map((rule, index) => (
          <li key={index} className="flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-muted-foreground" />
            {rule}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Pinned messages display
interface PinnedMessagesDisplayProps {
  messages: PinnedMessage[];
  className?: string;
}

export const PinnedMessagesDisplay: React.FC<PinnedMessagesDisplayProps> = ({
  messages,
  className,
}) => {
  if (messages.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {messages.map((message) => (
        <div
          key={message.id}
          className="flex items-start gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm"
        >
          <Pin className="w-3 h-3 mt-1 text-blue-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-xs">{message.sender_name}</p>
            <p className="text-muted-foreground">{message.content}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
